import React from 'react';
import { Link } from '@resy/ui-kitchen';
import PropTypes from 'prop-types';
import { updateLinksWithOriginB2B, getResyCopyright, handleMktAnalytics } from '../../AmexMarketingUtils/AmexUtils';
import AppleLogo from '../../../images/amexrga/apple.svg';
import AndroidLogo from '../../../images/amexrga/google-play-icon.svg';
import Facebook from '../../../images/amexrga/facebook.svg';
import Instagram from '../../../images/amexrga/instagram.svg';
import Twitter from '../../../../../../ui-kitchen/src/svg/icons/twitter.svg';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import './AmexGDAFooter.scss';
import CustomImage from '../CustomImage';
import { CTA_EVENT_NAME } from '../../AmexMarketingUtils/constants';

const AmexGDAFooter = ({
  footer, amexFooterImg, baseRequestUrl, androidAppLink, iOsAppLink, printContent, hasOriginB2B,
  Analytics,
}) => {
  const {
    ios, privacy, terms, cookie, copyright, headline, gda, dir, explore, gdab2b, facebook, instagram, twitter, amexLogoText, android, accessibility,
  } = { ...footer };

  const sanitizedCopyright = sanitizeString(copyright);
  const gdaPageLink = '/global-dining-access/#main-anchor';

  const renderCopyright = () => (
    <p
      dangerouslySetInnerHTML={{
        __html: printContent(getResyCopyright(sanitizedCopyright), 'copyright', true),
      }}
      className="GDA__Footer__Copy"
    />
  );

  const gdaLink = printContent(gda, 'gda');
  const dirLink = printContent(dir, 'dir');
  const exploreLink = printContent(explore, 'explore');
  const gdaB2b = printContent(gdab2b, 'gdab2b');
  const iosLink = printContent(ios, 'ios');

  const handleAnalytics = (linkName) => {
    const extraParams = {
      action_name: 'Footer Link Click',
      item_name: linkName,
    };
    handleMktAnalytics(Analytics.event, CTA_EVENT_NAME, extraParams);
  };

  return (
    <footer className="GDA__Footer" aria-label="Resy Global Dining Access footer">
      <div className="GDA__Footer__Logos">
        <p>{headline}</p>
        <p className="GDA__Footer--headline">
          <CustomImage key={amexFooterImg} imageName={amexFooterImg} baseRequestUrl={baseRequestUrl} fileType="svg" />
          <span>{printContent(amexLogoText, 'amexLogoText')}</span>
        </p>
      </div>

      <div className="GDA__Footer__Other-and-social">
        <p>
          <Link
            href={updateLinksWithOriginB2B(gdaPageLink, hasOriginB2B)}
            target="_blank"
            onContextMenu={() => handleAnalytics(gdaLink)}
            onClick={() => {
              handleAnalytics(gdaLink);
            }}
            className="GDA__Footer--links"
          >
            {gdaLink}
          </Link>
        </p>
        <p>
          <Link
            href={updateLinksWithOriginB2B('/global-dining-access/directory', hasOriginB2B)}
            target="_blank"
            onContextMenu={() => {
              handleAnalytics(dirLink);
            }}
            onClick={() => {
              handleAnalytics(dirLink);
            }}
            className="GDA__Footer--links"
          >
            {dirLink}
          </Link>
        </p>
        <p>
          <Link
            href="/"
            target="_blank"
            className="GDA__Footer--links"
            onContextMenu={() => handleAnalytics(exploreLink)}
            onClick={() => handleAnalytics(exploreLink)}
          >
            {exploreLink}
          </Link>
        </p>
        {(hasOriginB2B && gdab2b) && (
          <p>
            <Link
              href="/global-dining-access-partner-page"
              className="GDA__Footer--links"
              onContextMenu={() => handleAnalytics(gdaB2b)}
              onClick={() => handleAnalytics(gdaB2b)}
            >
              {gdaB2b}
            </Link>
          </p>
        )}
        <p className="GDA__Footer__Social">
          <Link
            className="GDA__Footer--facebook"
            href="https://www.facebook.com/Resy"
            target="_blank"
            aria-label={facebook}
            rel="noopener noreferrer"
            resy-track-link={facebook}
          >
            <Facebook />
          </Link>
          <Link
            href="https://www.instagram.com/resy"
            aria-label={instagram}
            className="GDA__Footer--instagram"
            target="_blank"
            rel="noopener noreferrer"
            resy-track-link={instagram}
          >
            <Instagram />
          </Link>
          <Link
            href="https://twitter.com/resy"
            aria-label={twitter}
            className="GDA__Footer--twitter"
            target="_blank"
            rel="noopener noreferrer"
            resy-track-link={twitter}
          >
            <Twitter />
          </Link>
        </p>
      </div>

      <div className="GDA__Footer__Apps-and-links">
        <div className="GDA__Footer__Apps">
          {(!iOsAppLink || iOsAppLink === '') && printContent(iOsAppLink, 'iOsAppLink')}
          <Link
            aria-label={ios}
            className="GDA__Footer--ios"
            target="_blank"
            rel="noopener noreferrer"
            resy-track-link={ios}
            href={iOsAppLink}
            onContextMenu={() => handleAnalytics(iosLink)}
            onClick={() => handleAnalytics(iosLink)}
          >
            <span className="GDA__Footer__App--icon"><AppleLogo /></span>
            <span className="GDA__Footer__App--text">{printContent(ios, 'ios')}</span>
          </Link>
          <span className="GDA__Footer__Separator" />
          {androidAppLink && (
            <Link
              href={androidAppLink}
              aria-label={android}
              className="GDA__Footer--android"
              rel="noopener noreferrer"
              resy-track-link={android}
              target="_blank"
            >
              <span className="GDA__Footer__App--icon"><AndroidLogo /></span>
              <span className="GDA__Footer__App--text">{printContent(android, 'android')}</span>
            </Link>
          )}
        </div>
        <p className="GDA__Footer__Links">
          <Link
            className="GDA__Footer__Link"
            href="privacy"
            target="_blank"
            onContextMenu={() => handleAnalytics(privacy)}
            onClick={() => handleAnalytics(privacy)}
          >
            {printContent(privacy, 'privacy')}
          </Link>
          <span className="GDA__Footer__Separator" aria-hidden="true">|</span>
          <Link
            className="GDA__Footer__Link"
            href="terms"
            target="_blank"
            onContextMenu={() => handleAnalytics(terms)}
            onClick={() => {
              handleAnalytics(terms);
            }}
          >
            {printContent(terms, 'terms')}
          </Link>
          <span className="GDA__Footer__Separator" aria-hidden="true">|</span>
          <Link
            className="GDA__Footer__Link"
            href="cookie-policy"
            target="_blank"
            onContextMenu={() => handleAnalytics(cookie)}
            onClick={() => handleAnalytics(cookie)}
          >
            {printContent(cookie, 'cookie')}
          </Link>
          <span className="GDA__Footer__Separator" aria-hidden="true">|</span>
          <Link
            className="GDA__Footer__Link"
            href="accessibility-statement"
            target="_blank"
            onContextMenu={() => handleAnalytics(accessibility)}
            onClick={() => handleAnalytics(accessibility)}
          >
            {printContent(accessibility, 'accessibility')}
          </Link>
        </p>
        {renderCopyright()}
      </div>
    </footer>
  );
};

AmexGDAFooter.propTypes = {
  footer: PropTypes.objectOf(PropTypes.string).isRequired,
  amexFooterImg: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  androidAppLink: PropTypes.string.isRequired,
  iOsAppLink: PropTypes.string.isRequired,
  printContent: PropTypes.func.isRequired,
  hasOriginB2B: PropTypes.bool.isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
};

export default AmexGDAFooter;
